import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ErrorBlank } from 'src/components';
import { BadgeV2 } from 'src/components/common/BadgeV2';
import Breadcrumb from 'src/components/common/Breadcrumb';
import { ButtonV2 } from 'src/components/common/ButtonV2';
import { IBBlank } from 'src/components/common/IBBlank';
import { Input } from 'src/components/common/Input';
import { Typography } from 'src/components/common/Typography';
import { FormInputField } from 'src/components/ib/FormInputField';
import IBLayout from 'src/components/ib/IBLayout';
import SVGIcon from 'src/components/icon/SVGIcon';
import { useIBInterviewDetail } from 'src/container/ib-interview';
import { useInterviewUpdateByTeacher } from 'src/generated/endpoint';
import { IBInterviewCommonQuestionDto, RequestCreateInterviewDtoCategory } from 'src/generated/model';

const PORTFOLIO_TYPES: { id: number; value: RequestCreateInterviewDtoCategory; name: string }[] = [
  { id: 1, value: 'CAS_PORTFOLIO_1', name: '1차 인터뷰' },
  { id: 2, value: 'CAS_PORTFOLIO_2', name: '2차 인터뷰' },
  { id: 3, value: 'CAS_PORTFOLIO_3', name: '3차 인터뷰' },
  { id: 4, value: 'CAS_RISK_ASSESSMENT', name: '위험평가' },
];

export const InterviewFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const { push } = useHistory();

  const { data, isLoading, refetch } = useIBInterviewDetail(Number(id));
  const { mutate: updateInterview, isLoading: updateInterviewLoading } = useInterviewUpdateByTeacher();

  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState<string>('');
  const [questions, setQuestions] = useState<IBInterviewCommonQuestionDto[]>([{ question: '', hint: '' }]);

  useEffect(() => {
    setTitle(data?.title || '');
    setQuestions(data?.commonQuestion || [{ question: '', hint: '' }]);
  }, [data]);

  if (isLoading || updateInterviewLoading) {
    return <IBBlank />;
  }

  if (data === undefined) {
    return <ErrorBlank text="인터뷰 데이터를 불러오는 과정에서 오류가 발생했습니다." />;
  }

  return (
    <div className="col-span-6 h-screen w-full">
      <IBLayout
        className="bg-gray-50"
        topBgColor="bg-white"
        topContent={
          <div>
            <div className="w-full pb-6 pt-16">
              <div className="flex flex-col items-start gap-3">
                <div className="flex w-full flex-row items-center justify-between">
                  <div className="flex flex-row gap-1">
                    <BadgeV2 color="gray" size={24} type="solid_regular">
                      프로젝트 관리
                    </BadgeV2>
                    <BadgeV2 color="dark_green" size={24} type="solid_strong" className="px-[12.5px]">
                      CAS
                    </BadgeV2>
                  </div>
                  <Breadcrumb
                    data={{
                      '프로젝트 관리': '/teacher/project',
                      '양식 상세': '',
                    }}
                  />
                </div>
                <div className="flex w-full items-center justify-between">
                  <Typography
                    variant="heading"
                    className="max-w-[692px] overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    양식
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        }
        hasContour={false}
        bottomContent={
          <div className="flex flex-grow flex-col">
            <div className="flex h-full flex-row gap-4 py-6">
              <div className="flex w-full flex-col rounded-xl bg-white p-6">
                <div>
                  {editMode ? (
                    <div className="flex flex-col gap-3 pt-4">
                      <div className="flex flex-col gap-4">
                        <Typography variant="title2" className="font-semibold">
                          제목
                        </Typography>
                        <Input.Basic
                          className="bg-white"
                          placeholder={'제목을 입력해주세요.'}
                          size={40}
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-10 pb-10 pt-6">
                        {questions.map((dto, index) => (
                          <FormInputField.Question
                            key={index}
                            index={index}
                            label="항목"
                            dto={dto}
                            setQuestion={(dto) =>
                              setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...dto } : q)))
                            }
                            deleteQuestion={() =>
                              setQuestions((prev) => {
                                const newQuestions = structuredClone(prev);
                                newQuestions.splice(index, 1);
                                return newQuestions;
                              })
                            }
                          />
                        ))}
                        <div className="flex justify-center">
                          <ButtonV2
                            variant="outline"
                            size={40}
                            color="gray400"
                            className="flex flex-row items-center gap-1 whitespace-pre"
                            onClick={() => setQuestions(questions.concat({ question: '', hint: '' }))}
                          >
                            <SVGIcon.Plus color="gray700" size={16} weight="bold" />
                            항목 추가하기
                          </ButtonV2>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <div className="flex flex-col items-start gap-1 border-b border-b-primary-gray-100 pb-6">
                        <Typography variant="title1">{data?.title}</Typography>
                        <Typography variant="body3" className="text-primary-gray-500">
                          {format(new Date(data?.updatedAt), 'yyyy.MM.dd')}
                        </Typography>
                      </div>
                      <div className="flex flex-col gap-10 pb-10 pt-6">
                        {data?.commonQuestion.map((item, index) => (
                          <div key={index} className="flex flex-col gap-3">
                            <div className="flex flex-row items-start">
                              <Typography variant="title3" className="text-primary-orange-800">
                                Q{index + 1}.&nbsp;
                              </Typography>
                              <Typography variant="title3">{item.question}</Typography>
                            </div>
                            <div className="rounded-lg border border-primary-gray-200 p-4">
                              <Typography variant="body2" className="font-medium">
                                {item.hint}
                              </Typography>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex w-full items-center justify-between">
                  {editMode ? (
                    <>
                      <ButtonV2 size={40} variant="solid" color="gray100" onClick={() => setEditMode(!editMode)}>
                        취소
                      </ButtonV2>
                      <ButtonV2
                        size={40}
                        variant="solid"
                        color="orange100"
                        onClick={() => {
                          updateInterview({
                            id: Number(id),
                            data: {
                              title: PORTFOLIO_TYPES.find((el) => el.value === data?.category)?.name || '인터뷰',
                              description: title,
                              commonQuestion: questions,
                            },
                          });
                          setEditMode(false);
                          refetch();
                        }}
                      >
                        저장하기
                      </ButtonV2>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-row items-center gap-2">
                        <ButtonV2 size={40} variant="outline" color="gray400" onClick={() => setEditMode(!editMode)}>
                          수정
                        </ButtonV2>
                      </div>
                      <ButtonV2
                        size={40}
                        variant="solid"
                        color="gray100"
                        onClick={() => push(`/teacher/ib/coordinatorPage/CAS`, { type: 'FORM' })}
                      >
                        목록 돌아가기
                      </ButtonV2>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};
